import React from "react";
import {
  Archive, BedDouble, CalendarCheck, ClipboardCheck, FlaskConical, IndianRupee, Layers, LockKeyhole, MapPin, Pill, Video,
} from "lucide-react";
import Reveal from "./Reveal";

const Card = ({ icon: Icon, title, body, tone = "brand", className = "", children, delay = 0 }) => {
  const tones = {
    brand: "bg-brand-50 text-brand-600",
    care: "bg-care-50 text-care-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
    sky: "bg-sky-50 text-sky-600",
  };
  return (
    <Reveal delay={delay} className={`group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 transition duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-soft ${className}`}>
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="mt-5 text-lg font-bold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
      {children}
    </Reveal>
  );
};

const Features = () => (
  <section id="features" className="relative bg-slate-50/70 py-24 sm:py-28">
    <div className="container-x">
      <Reveal className="mx-auto max-w-2xl text-center">
        <span className="eyebrow">Everything in one place</span>
        <h2 className="section-title">Replace five tools with one platform</h2>
        <p className="mt-4 text-lg text-slate-600">From the first booking to the final bill, every step of care is connected — so your team spends time with patients, not paperwork.</p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-6">
        <Card
          className="lg:col-span-4"
          icon={CalendarCheck}
          title="Smart appointments & scheduling"
          body="Doctor rosters become bookable slots automatically. Patients book online, the front desk books walk-ins, and double-booking is impossible."
        >
          <div className="mt-6 grid grid-cols-3 gap-2 sm:grid-cols-6">
            {["09:00", "09:15", "09:30", "09:45", "10:00", "10:15", "10:30", "10:45", "11:00", "11:15", "11:30", "11:45"].map((t, i) => (
              <span
                key={t}
                className={`rounded-lg border px-2 py-1.5 text-center text-xs font-medium ${
                  [1, 2, 5, 8].includes(i) ? "border-slate-200 bg-slate-100 text-slate-400 line-through" : i === 3 ? "border-brand-500 bg-brand-500 text-white" : "border-brand-200 bg-brand-50 text-brand-700"
                }`}
              >
                {t}
              </span>
            ))}
          </div>
        </Card>
        <Card
          className="lg:col-span-2"
          tone="violet"
          icon={Video}
          title="Built-in teleconsultation"
          body="HD video visits with a waiting room — the doctor writes the prescription without leaving the call."
        >
          <div className="mt-6 flex items-center gap-3 rounded-xl bg-ink-900 p-3 text-white">
            <span className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-rose-400" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-500" />
            </span>
            <span className="text-xs font-medium">Live · 12:48</span>
            <span className="ml-auto rounded-md bg-white/10 px-2 py-1 text-[11px]">Rx in-call</span>
          </div>
        </Card>

        <Card
          className="lg:col-span-2"
          tone="care"
          icon={BedDouble}
          title="Inpatient (IPD) & bed board"
          body="See every ward at a glance, admit to a bed, transfer, chart vitals and discharge — bed charges are calculated for you."
        >
          <div className="mt-6 grid grid-cols-6 gap-1.5">
            {Array.from({ length: 18 }).map((_, i) => (
              <span key={i} className={`h-5 rounded ${[2, 7, 11, 15].includes(i) ? "bg-care-200" : i === 9 ? "bg-amber-200" : "bg-brand-400/80"}`} />
            ))}
          </div>
        </Card>
        <Card
          className="lg:col-span-4"
          tone="amber"
          icon={IndianRupee}
          title="Billing that matches the care given"
          body="OPD bills at the counter and IPD bills generated at discharge — with per-item GST, advances and deposits, partial payments and printable receipts."
        >
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              ["Bed charges · ICU", "3 days", "₹18,000"],
              ["Lipid profile", "GST 5%", "₹945"],
              ["Advance received", "UPI", "– ₹10,000"],
            ].map(([a, b, c]) => (
              <div key={a} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                <p className="text-xs text-slate-500">{a}</p>
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-[11px] text-slate-400">{b}</span>
                  <span className="text-sm font-bold text-ink-900">{c}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-2" icon={ClipboardCheck} title="Structured e-prescriptions" body="SNOMED CT coded diagnoses, medicines and advice, shared with the patient instantly and saved to their record." delay={0.05} />
        <Card className="lg:col-span-2" tone="rose" icon={Pill} title="Pharmacy & stock" body="Medicines with batch, expiry, HSN and GST. Stock drops automatically when you bill or dispense to a ward." delay={0.1} />
        <Card className="lg:col-span-2" tone="sky" icon={Archive} title="Medical racks" body="Know exactly which rack holds what, with low-stock and empty-rack alerts for the pharmacy team." delay={0.15} />

        <Card className="lg:col-span-2" tone="care" icon={FlaskConical} title="Diagnostics & packages" body="Lab tests with home or lab collection, and health-check packages priced with automatic discounts." delay={0.05} />
        <Card className="lg:col-span-2" tone="violet" icon={MapPin} title="Multi-location ready" body="Run one clinic or a group of hospitals — each location with its own staff, wards, prices and reports." delay={0.1} />
        <Card className="lg:col-span-2" icon={LockKeyhole} title="Roles & permissions" body="Decide who can view, create or edit each module. Sub-admins only see what you allow." delay={0.15} />
      </div>

      <Reveal className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
        <Layers className="h-4 w-4 text-brand-500" /> Every module shares one patient record — no duplicate data entry.
      </Reveal>
    </div>
  </section>
);

export default Features;
